/**
 * Turns raw form values into something a salesperson can read.
 *
 * The sites store qualifying answers as enum constants — `SNACKS_DRYCK`,
 * `BAR_NATTKLUBB`, `VET_EJ` — and different sites (and different eras of the
 * same site) use different casings for the same concept. Forwarding those
 * verbatim makes a lead read like a database dump.
 *
 * Values stay in Swedish because that is the domain vocabulary the partner
 * works in; only the formatting is normalized.
 *
 * Unknown values fall back to a generic prettifier, so a site adding a new
 * option degrades to "Snacks Dryck" rather than breaking or vanishing.
 */

/** Keyed by the value lowercased with non-alphanumerics stripped. */
const VALUE_LABELS: Record<string, string> = {
  // --- intent: what they want to do ---------------------------------------
  kopa: "Köpa",
  köpa: "Köpa",
  leasa: "Leasa",
  hyra: "Hyra",
  vetej: "Vet ej",
  bokademo: "Boka demo",

  // --- placement: where the machine goes -----------------------------------
  kontor: "Kontor",
  offentlig: "Offentlig miljö",
  offentligmiljo: "Offentlig miljö",
  barnattklubb: "Bar / nattklubb",
  gym: "Gym",
  skola: "Skola",
  sjukhus: "Sjukhus",
  sjukhusvard: "Sjukhus & vård",
  butik: "Butik",
  hotell: "Hotell",
  restaurang: "Restaurang",
  lager: "Lager",
  industri: "Industri",
  padel: "Padelhall",
  padelhall: "Padelhall",
  annat: "Annat",

  // --- machine type ---------------------------------------------------------
  snacksdryck: "Snacks & dryck",
  snacks: "Snacks",
  dryck: "Dryck",
  maltider: "Måltider",
  måltider: "Måltider",
  kaffe: "Kaffe",
  fika: "Fika",
  snusautomatpremium: "Snusautomat Premium",
  snusautomatstandard: "Snusautomat Standard",
  snusautomatkompakt: "Snusautomat Kompakt",
  snusautomat: "Snusautomat",
  tobaksautomat: "Tobaksautomat",
  pizzaautomat: "Pizzaautomat",
  kombination: "Kombination",

  // --- misc answers ---------------------------------------------------------
  kort: "Kort",
  app: "App",
  swish: "Swish",
  subvention: "Subventionerad",
  delvis: "Delvis",
  ja: "Ja",
  nej: "Nej",
  smorgas: "Smörgåsar",
  smorgasar: "Smörgåsar",
  protein: "Protein",
};

function key(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9åäöéü]/gi, "");
}

/**
 * Generic fallback for values with no explicit label: split on underscores and
 * dashes, then title-case. `SNACKS_DRYCK` -> `Snacks Dryck`.
 */
function prettify(value: string): string {
  const trimmed = value.trim();

  // Leave anything that already looks like prose or an identifier alone.
  if (/\s/.test(trimmed) && !/_/.test(trimmed)) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Hostnames such as `hotellautomat.se` are identifiers, not enum values.
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(trimmed)) return trimmed;

  const words = trimmed.split(/[_\-·]+/).filter(Boolean);
  if (words.length === 0) return trimmed;

  return words
    .map((word) => {
      // Preserve words that already have deliberate mixed casing.
      if (/[a-zåäö]/.test(word) && /[A-ZÅÄÖ]/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function humanizeValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return VALUE_LABELS[key(trimmed)] ?? prettify(trimmed);
}

/**
 * Column name -> the label shown to the partner.
 *
 * Shared across every site: the collections were generated from the same form
 * template, so one table covers all of them and a new site inherits it for free.
 */
export const DETAIL_LABELS: Record<string, string> = {
  intent: "Looking to",
  placement: "Placement",
  venuetype: "Venue type",
  industry: "Industry",
  city: "City",
  users: "People on site",
  dailyusers: "People on site",
  employeecount: "Company size",
  role: "Their role",
  orgnumber: "Org. number",
  foodtype: "Food type",
  paymentneed: "Payment method",
  payment: "Payment method",
  service: "Service level",
  machineinterest: "Interested in",
  // third template (hotell / kontor / glass / tcg)
  staffedhours: "Staffed hours",
  rooms: "Rooms",
  employees: "Company size",
  season: "Season",
  sellstcg: "Sells TCG today",
  // added to every site's form in Aug 2026; the domain the form lives on
  sourcesite: "Source site",
};

/**
 * Display order for qualifying answers, most decision-relevant first. A
 * salesperson reads the top two lines and knows whether to call.
 * Anything not listed sorts after these, alphabetically.
 */
export const DETAIL_ORDER: readonly string[] = [
  "intent",
  "placement",
  "machineinterest",
  "venuetype",
  "industry",
  "users",
  "dailyusers",
  "employeecount",
  "role",
  "foodtype",
  "paymentneed",
  "payment",
  "service",
  "orgnumber",
  "staffedhours",
  "rooms",
  "employees",
  "season",
  "sellstcg",
  "sourcesite",
];

/**
 * Columns never forwarded to a partner: internal bookkeeping, or data already
 * shown elsewhere in the email.
 */
export const DETAIL_EXCLUDED: ReadonlySet<string> = new Set([
  "status", // always "NEW" — our own pipeline state, not theirs
  "sourcepage", // shown under attribution
  "utmsource",
  "utmmedium",
  "utmcampaign",
  // GDPR checkbox bookkeeping, added to every form in Aug 2026. The boolean
  // stringifies to "False" on older leads and the text is the checkbox's own
  // legal sentence — neither helps a salesperson.
  "consent",
  "consenttext",
  "consentat",
]);

/** Humanize a column name we have no explicit label for. */
export function humanizeLabel(column: string): string {
  const spaced = column
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return column;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
